import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  Canceler,
  InternalAxiosRequestConfig
} from "axios";
import { Modal } from "antd";
import { removeToken, getToken } from "./util";
import { clearTokens } from '@/store/user/userInfo'

import { store } from '@/store/index'

// 定义响应数据的通用结构
interface ResponseData<T = any> {

  config?: T;
  data: T;
  status: number;
  headers: T;
  request: T;
  statusText: string;
}

// 定义队列类型
interface RequestQueue {
  [url: string]: Canceler;
}

const HTTP_ERROR_MAP: Record<number, string> = {
  400: "请求参数错误",
  401: "登录信息已过期，请重新登录",
  403: "没有权限访问",
  404: "请求的接口不存在",
  405: "请求方法不被允许",
  408: "请求超时",
  500: "服务器内部错误",
  502: "网关错误",
  503: "服务暂不可用",
  504: "网关超时",
};

let errorModal: ReturnType<typeof Modal.error> | null = null;

function showRequestError(content: string, title = "请求失败") {
  const message = (content || "").trim() || "接口请求失败";
  if (errorModal) {
    errorModal.destroy();
  }
  errorModal = Modal.error({
    title,
    content: message,
    okText: "知道了",
    afterClose: () => {
      errorModal = null;
    },
  });
}

function pickBackendMessage(data: any): string {
  if (!data) return "";
  if (typeof data === "string") return data;
  const msg = data?.status?.message || data?.message || data?.msg || data?.error;
  return typeof msg === "string" ? msg : "";
}

function getRequestErrorMessage(error: any): string {
  const data = error?.response?.data;
  const backendMsg = pickBackendMessage(data);
  if (backendMsg) return backendMsg;

  const status = error?.response?.status;
  if (status && HTTP_ERROR_MAP[status]) {
    return `${HTTP_ERROR_MAP[status]}（${status}）`;
  }
  if (error?.code === "ECONNABORTED" || String(error?.message || "").includes("timeout")) {
    return "请求超时，请稍后重试";
  }
  if (error?.message === "Network Error") {
    return "网络异常，请检查网络连接";
  }
  return error?.message || "接口请求失败";
}

function buildErrorResult(code: string | number, message: string) {
  return {
    result: {},
    status: {
      code: String(code),
      message,
    },
    msg: message,
  };
}

function forceLogout() {
  removeToken("token");
  localStorage.removeItem("userInfo");
  localStorage.removeItem("tokenExpire");
  store.dispatch(clearTokens());
  setTimeout(() => {
    window.location.href = "/login";
  }, 1500);
}

class Axios {
  private baseUrl: string;
  private queue: RequestQueue;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.queue = {};
  }

  /**
   * 从队列中移除请求
   */
  destroy(url: string): number {
    delete this.queue[url];
    const queue = Object.keys(this.queue);
    return queue.length;
  }

  /**
   * 获取内部配置
   */
  private getInsideConfig(): AxiosRequestConfig {
    return {
      baseURL: this.baseUrl,
      headers: {
        "Content-Type": "application/json;charset=utf-8",
      },
      // 添加 token 到请求头
      transformRequest: [
        (data: any, headers: any) => {
          const token = getToken('token');
          if (token) {
            headers["Authorization"] = `${token}`;
          }
          return (data);
        },
      ],
    };
  }

  /**
   * 拦截器设置
   */
  private interceptors(instance: AxiosInstance, url?: string): void {
    // 请求拦截
    instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // 添加取消令牌
        if (url) {
          config.cancelToken = new axios.CancelToken((cancel: Canceler) => {
            this.queue[url] = cancel;
          });
        }
        return config;
      },
      (error: any) => {
        return Promise.reject(error);
      }
    );

    // 响应拦截
    instance.interceptors.response.use(
      (res) => {
        if (url) {
          this.destroy(url);
        }

        if (res.config?.responseType === 'blob') {
          return res;
        }

        const { data } = res;
        const bizCode = data?.status?.code;
        const bizMessage = pickBackendMessage(data) || "接口请求失败";

        // 处理业务逻辑错误
        if (bizCode === "-100002") {
          showRequestError(bizMessage || "登录过期，请重新登录", "登录过期");
          forceLogout();
          return Promise.reject(buildErrorResult(bizCode, bizMessage));
        }

        if (bizCode !== undefined && bizCode !== null && String(bizCode) !== "0") {
          showRequestError(bizMessage);
        }

        // 返回响应数据
        return data;
      },
      (error: any) => {
        if (url) {
          this.destroy(url);
        }

        if (axios.isCancel(error)) {
          return new Promise(() => { });
        }

        const errorMessage = getRequestErrorMessage(error);
        const httpStatus = error?.response?.status;

        showRequestError(errorMessage);

        if (httpStatus === 401 || httpStatus === 403) {
          forceLogout();
        }

        return buildErrorResult(httpStatus || "error", errorMessage);
      }
    );
  }

  /**
   * 发起请求
   */
  request<T = any>(options: AxiosRequestConfig): Promise<ResponseData<T>> {
    const instance: AxiosInstance = axios.create();
    const config = Object.assign(this.getInsideConfig(), options);
    const requestUrl = String(options.url || '');
    if (!config.timeout && requestUrl.includes('/searchMetas')) {
      config.timeout = 5000;
    }
    this.interceptors(instance, options.url);
    return instance(config) as Promise<ResponseData<T>>;
  }

  /**
   * 取消所有请求
   */
  cancelAll(): void {
    Object.keys(this.queue).forEach((url: string) => {
      this.queue[url]("取消请求");
      delete this.queue[url];
    });
  }

  /**
   * 取消特定请求
   */
  cancelRequest(url: string): void {
    if (this.queue[url]) {
      this.queue[url]("取消请求");
      this.destroy(url);
    }
  }
}

export default Axios;